import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';

const C = {
  black:  '#09080c',
  deep:   '#110f16',
  card:   '#15121e',
  raised: '#1d1929',
  violet: '#5a3d99',
  accent: '#7c5cbf',
  lilac:  '#c9a0e8',
  ivory:  '#f2ece0',
  muted:  'rgba(242,236,224,0.38)',
  faint:  'rgba(242,236,224,0.08)',
  border: 'rgba(242,236,224,0.07)',
  gold:   '#e2b96a',
};
const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};

export default function Auth(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next') || '/studio';
  const checkoutSuccess = new URLSearchParams(location.search).get('checkout') === 'success';
  const [mode, setMode]       = useState('signup'); // signin | signup | reset
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'signin') {
        const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // Link any guest subscription made before logging in
        try {
          const token = signInData?.session?.access_token;
          if (token) {
            await fetch(`${process.env.REACT_APP_API_URL}/api/subscription/link-guest`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ email }),
            });
          }
        } catch (e) {
          console.log('link-guest on login (non-fatal):', e.message);
        }
        navigate(next);
      } else if (mode === 'signup') {
        const { data: signUpData, error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: `${window.location.origin}${next}` } });
        // If coming from guest checkout, link the pending subscription
        if (!error && signUpData?.session) {
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/subscription/link-guest`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${signUpData.session.access_token}`,
              },
            });
          } catch (_) {}
        }
        if (error) throw error;
        navigate(next || '/account');
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/studio` });
        if (error) throw error;
        setSuccess('Password reset email sent.');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (filled) => ({
    width: '100%',
    padding: '14px 16px',
    background: C.raised,
    border: `1px solid ${filled ? 'rgba(124,92,191,0.45)' : 'rgba(124,92,191,0.18)'}`,
    borderRadius: '13px',
    color: C.ivory,
    fontSize: '15px',
    fontFamily: font.body,
    fontStyle: 'italic',
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: C.black,
      color: C.ivory,
      fontFamily: font.ui,
      fontWeight: 300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Glow */}
      <div style={{
        position: 'absolute', width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(90,61,153,0.12) 0%, transparent 65%)',
        top: '-80px', left: '50%', transform: 'translateX(-50%)',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div
          onClick={() => navigate('/')}
          style={{ textAlign: 'center', marginBottom: '36px', cursor: 'pointer' }}
        >
          <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '42px' }}>
            Your<span style={{ color: C.lilac }}>Siren</span>
          </div>
          <div style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.22)', marginTop: '6px' }}>
            your story. your words. your desire.
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: '24px',
          padding: '32px 28px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}>
          {/* Title */}
          <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '26px', marginBottom: '4px' }}>
            {mode === 'signin' ? 'Welcome back' : mode === 'signup' ? 'Begin your story' : 'Reset password'}
          </div>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '24px', lineHeight: 1.6 }}>
            {mode === 'signin' ? 'Sign in to hear your stories.' : mode === 'signup' ? 'Create your account to get started.' : 'We\'ll send you a reset link.'}
          </div>

          {/* Fields */}
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email address…"
            style={inputStyle(!!email)}
          />
          {mode !== 'reset' && (
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password…"
              style={inputStyle(!!password)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          )}

          {error && (
            <div style={{
              color: '#e88a8a', fontSize: '13px', marginBottom: '14px',
              padding: '10px 14px', background: 'rgba(232,138,138,0.08)',
              borderRadius: '10px', border: '1px solid rgba(232,138,138,0.15)',
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              color: C.lilac, fontSize: '13px', marginBottom: '14px',
              padding: '10px 14px', background: 'rgba(124,92,191,0.08)',
              borderRadius: '10px', border: '1px solid rgba(124,92,191,0.2)',
            }}>{success}</div>
          )}

          {/* Primary button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%', padding: '16px',
              background: loading ? 'rgba(90,61,153,0.3)' : 'linear-gradient(135deg, #5a3d99, #7c5cbf)',
              border: 'none', borderRadius: '13px',
              color: C.ivory, fontFamily: font.display, fontStyle: 'italic', fontSize: '19px',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 8px 28px rgba(90,61,153,0.3)',
              marginBottom: '16px',
            }}
          >
            {loading ? '…' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link'}
          </button>

          {/* Mode switches */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
            {mode === 'signin' && (
              <>
                <button onClick={() => { setMode('signup'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: font.ui }}>
                  {mode === 'signup' ? 'Already have an account? ' : 'New here? '}<span style={{ color: C.lilac }}>{mode === 'signup' ? 'Sign in' : 'Create one'}</span>
                </button>
                <button onClick={() => { setMode('reset'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.2)', fontSize: '12px', cursor: 'pointer', fontFamily: font.ui }}>
                  Forgot password?
                </button>
              </>
            )}
            {mode !== 'signin' && (
              <button onClick={() => { setMode('signin'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: font.ui }}>
                ← Back to sign in
              </button>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '11px', color: 'rgba(242,236,224,0.1)', letterSpacing: '1px' }}>
          For adults only · 18+
        </div>
      </div>
    </div>
  );
}
